import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { MENU_CACHE_LIMIT } from './cachedMenu.constants';
import {
  IAddBrandMenuData,
  IAddMenuData,
  ICachedMenuSlice,
  ISetBrandMenuData,
  ISetBrandMenuUtterancesData,
  ISetBrandMenuVersionForRestaurantData,
  IUpdateBrandMenuData,
  IUpdateMenuData,
} from './cachedMenu.props';
import { IUtterance } from '../../../types/brandMenu';

const initialState: ICachedMenuSlice = {
  menusById: {},
  menusOrder: [],
  brandMenusById: {},
  brandMenusOrder: [],
  brandMenuUtterancesById: {},
  brandMenuCache: {},
  brandMenuCacheOrder: [],
  brandMenuVersionByRestaurant: {},
};

const cachedMenuSlice = createSlice({
  name: 'cachedMenu',
  initialState,
  reducers: {
    resetCachedMenuData: () => {
      return { ...initialState };
    },
    addMenuData: (state, { payload }: PayloadAction<IAddMenuData>) => {
      const { restaurantId, menuVersion, menuData, menuTitle } = payload;
      if (state.menusOrder.length === MENU_CACHE_LIMIT) {
        // Remove the oldest cached menu restaurant data
        const oldestDetails = state.menusOrder.shift();
        if (oldestDetails?.restaurantId)
          delete state.menusById[oldestDetails.restaurantId];
      }
      state.menusOrder.push({ restaurantId, menuVersion, menuTitle });
      state.menusById[restaurantId] = menuData;
    },
    updateMenuData: (state, { payload }: PayloadAction<IUpdateMenuData>) => {
      const { restaurantId, menuVersion, menuData, index, menuTitle } = payload;
      state.menusOrder[index] = { restaurantId, menuVersion, menuTitle };
      state.menusById[restaurantId] = menuData;
    },
    addBrandMenuData: (
      state,
      { payload }: PayloadAction<IAddBrandMenuData>
    ) => {
      const { restaurantId, menuVersion, brandMenuData } = payload;
      if (state.brandMenusOrder.length === MENU_CACHE_LIMIT) {
        const oldestDetails = state.brandMenusOrder.shift();
        if (oldestDetails?.restaurantId) {
          delete state.brandMenusById[oldestDetails.restaurantId];
          delete state.brandMenuUtterancesById[oldestDetails.restaurantId];
        }
      }
      state.brandMenusOrder.push({ restaurantId, menuVersion });
      state.brandMenusById[restaurantId] = brandMenuData;
    },
    updateBrandMenuData: (
      state,
      { payload }: PayloadAction<IUpdateBrandMenuData>
    ) => {
      const { restaurantId, menuVersion, brandMenuData, index } = payload;
      state.brandMenusOrder[index] = { restaurantId, menuVersion };
      state.brandMenusById[restaurantId] = brandMenuData;
    },
    cacheBrandMenuUtterances: (
      state,
      {
        payload,
      }: PayloadAction<{ restaurantId: string; utterances: IUtterance[] }>
    ) => {
      state.brandMenuUtterancesById[payload.restaurantId] = payload.utterances;
    },
    // Brand menu V2 — cache the brand menu keyed by brand → version, so multiple
    // versions of one brand can coexist. A new (brand, version) pair is added
    // (evicting the oldest pair at the limit); re-caching the same pair just
    // overwrites its entry. Eviction cleans up an emptied brand sub-map.
    setBrandMenu: (state, { payload }: PayloadAction<ISetBrandMenuData>) => {
      const { brandKey, version, brandMenu } = payload;
      const isNewPair =
        state.brandMenuCacheOrder.findIndex(
          (entry) => entry.brandKey === brandKey && entry.version === version
        ) === -1;

      if (isNewPair) {
        if (state.brandMenuCacheOrder.length === MENU_CACHE_LIMIT) {
          const oldest = state.brandMenuCacheOrder.shift();
          if (oldest) {
            const versions = state.brandMenuCache[oldest.brandKey];
            if (versions) {
              delete versions[oldest.version];
              if (Object.keys(versions).length === 0) {
                delete state.brandMenuCache[oldest.brandKey];
              }
            }
          }
        }
        state.brandMenuCacheOrder.push({ brandKey, version });
      }

      if (!state.brandMenuCache[brandKey]) state.brandMenuCache[brandKey] = {};
      // Reset the entry (utterances arrive asynchronously via setBrandMenuUtterances).
      state.brandMenuCache[brandKey][version] = { brandMenu };
    },
    // Fold the async-fetched intent utterances into the (brand, version) entry,
    // but only if it still exists — guards against a write landing after the
    // entry was evicted or the restaurant moved to a different version.
    setBrandMenuUtterances: (
      state,
      { payload }: PayloadAction<ISetBrandMenuUtterancesData>
    ) => {
      const { brandKey, version, utterances } = payload;
      const entry = state.brandMenuCache[brandKey]?.[version];
      if (entry) {
        entry.utterances = utterances;
      }
    },
    // Record which brand-menu version a restaurant is currently on (per-restaurant,
    // since versions can diverge). Feeds getMenuVersion; written on every load.
    setBrandMenuVersionForRestaurant: (
      state,
      { payload }: PayloadAction<ISetBrandMenuVersionForRestaurantData>
    ) => {
      state.brandMenuVersionByRestaurant[payload.restaurantCode] =
        payload.version;
    },
  },
});

export const {
  resetCachedMenuData,
  addMenuData,
  updateMenuData,
  addBrandMenuData,
  updateBrandMenuData,
  cacheBrandMenuUtterances,
  setBrandMenu,
  setBrandMenuUtterances,
  setBrandMenuVersionForRestaurant,
} = cachedMenuSlice.actions;
export default cachedMenuSlice.reducer;
